
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { 
  EventImage, 
  uploadImageToStorage, 
  saveImageToDatabase, 
  removeImageFromStorage, 
  removeImageFromDatabase,
  updatePrimaryImage,
  fetchEventImages
} from "./imageUploadUtils";

interface UseImageUploadProps {
  imageUrl: string;
  imageUrls: string[];
  setImageUrl: (url: string) => void;
  setImageUrls: (urls: string[]) => void;
  eventId?: string;
  eventImages?: EventImage[];
  setEventImages?: (images: EventImage[] | ((prev: EventImage[]) => EventImage[])) => void;
}

export const useImageUpload = ({ 
  imageUrl, 
  imageUrls, 
  setImageUrl, 
  setImageUrls, 
  eventId,
  eventImages,
  setEventImages
}: UseImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localEventImages, setLocalEventImages] = useState<EventImage[]>([]);
  const { toast } = useToast();

  // Use provided eventImages or local state; ensure it's always an array
  const hookEventImages = Array.isArray(eventImages) ? eventImages : localEventImages;
  
  // Function to update event images with proper type checking
  const setHookEventImages = (images: EventImage[] | ((prev: EventImage[]) => EventImage[])) => {
    try {
      if (setEventImages) {
        if (typeof images === 'function') {
          setEventImages((prev) => {
            // Make sure prev is always an array
            const safeImages = Array.isArray(prev) ? prev : [];
            try {
              const updatedImages = (images as (prev: EventImage[]) => EventImage[])(safeImages);
              return Array.isArray(updatedImages) ? updatedImages : [];
            } catch (error) {
              console.error('Error updating images:', error);
              return safeImages;
            }
          });
        } else {
          setEventImages(Array.isArray(images) ? images : []);
        }
      } else {
        if (typeof images === 'function') {
          setLocalEventImages(prev => {
            try {
              const updatedImages = (images as (prev: EventImage[]) => EventImage[])(prev);
              return Array.isArray(updatedImages) ? updatedImages : [];
            } catch (error) {
              console.error('Error updating local images:', error);
              return prev;
            }
          });
        } else {
          setLocalEventImages(Array.isArray(images) ? images : []);
        }
      }
    } catch (error) {
      console.error('Error in setHookEventImages:', error);
      toast({
        title: "Error",
        description: "Failed to update images",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (eventId) {
      loadExistingImages();
    } else if (imageUrl || imageUrls.length > 0) {
      try {
        const allImages = [
          ...(imageUrl ? [imageUrl] : []),
          ...imageUrls.filter(url => url !== imageUrl)
        ];
        
        const tempEventImages: EventImage[] = allImages.map((url, index) => ({
          image_id: `temp-${index}`,
          image_url: url,
          is_primary: url === imageUrl,
          display_order: index
        }));
        
        setHookEventImages(tempEventImages);
      } catch (error) {
        console.error('Error setting initial event images:', error);
      }
    }
  }, [eventId]);

  const loadExistingImages = async () => {
    if (!eventId) return;
    
    setIsLoading(true);
    try {
      const images = await fetchEventImages(eventId);
      
      if (images && images.length > 0) {
        setHookEventImages(images);
        
        const primaryImage = images.find(img => img.is_primary);
        if (primaryImage) {
          setImageUrl(primaryImage.image_url);
        } else if (images.length > 0) {
          setImageUrl(images[0].image_url);
        }
        
        const nonPrimaryUrls = images
          .filter(img => !img.is_primary)
          .map(img => img.image_url);
        
        setImageUrls(nonPrimaryUrls);
      } else if (imageUrl) {
        const tempImage: EventImage = {
          image_id: `temp-legacy`,
          image_url: imageUrl,
          is_primary: true,
          display_order: 0
        };
        
        setHookEventImages([tempImage]);
        
        if (eventId) {
          try {
            const savedImage = await saveImageToDatabase(eventId, imageUrl, true, 0);
            if (savedImage) {
              setHookEventImages([savedImage]);
            }
          } catch (error) {
            console.error('Error saving image to database:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing images:', error);
      toast({
        title: "Error",
        description: "Failed to load existing images",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Make sure allImages is always valid
  const allImages = hookEventImages.length > 0
    ? hookEventImages.sort((a, b) => a.display_order - b.display_order).map(img => img.image_url)
    : imageUrl 
      ? [imageUrl, ...imageUrls.filter(url => url !== imageUrl)] 
      : imageUrls;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const newImages: EventImage[] = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        try {
          const file = files[i];
          
          const publicUrl = await uploadImageToStorage(file);
          if (!publicUrl) continue;
          
          const isPrimary = hookEventImages.length === 0 && i === 0 && !imageUrl;
          const displayOrder = hookEventImages.length + i;
          
          let newImage: EventImage = {
            image_id: `temp-${crypto.randomUUID()}`,
            image_url: publicUrl,
            is_primary: isPrimary,
            display_order: displayOrder
          };
          
          if (eventId) {
            const savedImage = await saveImageToDatabase(eventId, publicUrl, isPrimary, displayOrder);
            if (savedImage) {
              newImage = savedImage;
            }
          }
          
          newImages.push(newImage);
        } catch (fileError) {
          console.error('Error processing file:', fileError);
        }
      }
      
      // Update event images with the new ones
      setHookEventImages((prev: EventImage[]) => {
        const safeImages = Array.isArray(prev) ? prev : [];
        return [...safeImages, ...newImages];
      });
      
      if (newImages.length > 0) {
        if (!imageUrl && newImages.some(img => img.is_primary)) {
          const newPrimary = newImages.find(img => img.is_primary);
          if (newPrimary) {
            setImageUrl(newPrimary.image_url);
          }
        }
        
        const currentNonPrimary = allImages.filter(url => url !== imageUrl);
        const newNonPrimary = newImages
          .filter(img => !img.is_primary)
          .map(img => img.image_url);
        
        setImageUrls([...currentNonPrimary, ...newNonPrimary]);
      }
      
      toast({
        title: "Success",
        description: `${newImages.length} image${newImages.length === 1 ? '' : 's'} uploaded successfully`,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async (urlToRemove: string) => {
    try {
      const imageToRemove = hookEventImages.find(img => img.image_url === urlToRemove);
      if (!imageToRemove) return;
      
      const isPrimary = imageToRemove.is_primary;
      
      await removeImageFromStorage(urlToRemove);

      if (eventId) {
        await removeImageFromDatabase(eventId, urlToRemove);
      }

      // Filter out the removed image
      const updatedEventImages = hookEventImages.filter(img => img.image_url !== urlToRemove);
      
      if (isPrimary && updatedEventImages.length > 0) {
        const newPrimaryImage = updatedEventImages[0];
        
        if (eventId) {
          await updatePrimaryImage(eventId, newPrimaryImage.image_url);
        }
        
        // Update event images making the first one primary
        setHookEventImages(updatedEventImages.map((img, idx) => ({
          ...img,
          is_primary: idx === 0
        })));
        
        setImageUrl(newPrimaryImage.image_url);
        setImageUrls(updatedEventImages.slice(1).map(img => img.image_url));
      } else if (isPrimary) {
        setHookEventImages([]);
        setImageUrl('');
        setImageUrls([]);
      } else {
        setHookEventImages(updatedEventImages);
        setImageUrls(updatedEventImages
          .filter(img => !img.is_primary)
          .map(img => img.image_url));
      }

      toast({
        title: "Success",
        description: "Image removed successfully",
      });
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: "Error",
        description: "Failed to remove image",
        variant: "destructive",
      });
    }
  };

  const handleSetPrimaryImage = async (url: string) => {
    try {
      const imageToSetPrimary = hookEventImages.find(img => img.image_url === url);
      if (!imageToSetPrimary || imageToSetPrimary.is_primary) return;
      
      if (eventId) {
        await updatePrimaryImage(eventId, url);
      }
      
      // Update all images changing which one is primary
      const updatedImages = hookEventImages.map(img => ({
        ...img,
        is_primary: img.image_url === url
      }));
      
      setHookEventImages(updatedImages);
      
      setImageUrl(url);
      setImageUrls(updatedImages
        .filter(img => img.image_url !== url)
        .map(img => img.image_url));
      
      toast({
        title: "Success",
        description: "Primary image updated",
      });
    } catch (error) {
      console.error('Error setting primary image:', error);
      toast({
        title: "Error",
        description: "Failed to update primary image",
        variant: "destructive",
      });
    }
  };

  return {
    isUploading,
    isLoading,
    eventImages: hookEventImages,
    allImages,
    handleImageUpload,
    handleRemoveImage,
    handleSetPrimaryImage
  };
};
